import { useEffect } from "react";

interface StructuredDataProps {
  type?: "Person" | "Article" | "WebSite";
  data?: Record<string, unknown>;
}

const personData = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Brian J Kopp",
  jobTitle: "Digital Transformation Executive",
  description:
    "Senior Digital Transformation Executive driving $16M+ in annual impact through Product Strategy, AI Innovation, and Operational Excellence in Healthcare & MarTech.",
  url: "https://brianjkopp.co",
  image: "https://brianjkopp.co/og-image.png",
  sameAs: ["https://www.linkedin.com/in/brianjkopp/"],
  knowsAbout: [
    "Digital Transformation",
    "Product Strategy",
    "AI Innovation",
    "Healthcare Technology",
    "MarTech",
    "Operational Excellence",
    "Enterprise Software",
    "Change Management",
  ],
  alumniOf: {
    "@type": "EducationalOrganization",
    name: "University",
  },
  worksFor: {
    "@type": "Organization",
    name: "Professional Services",
  },
};

const websiteData = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "Brian J Kopp | Digital Transformation Executive",
  url: "https://brianjkopp.co",
  description:
    "Turning Digital Operations into Strategic Growth Engines. $16M+ in annual impact through Product Strategy, AI Innovation, and Operational Excellence.",
  author: {
    "@type": "Person",
    name: "Brian J Kopp",
  },
};

export function StructuredData({ type = "Person", data }: StructuredDataProps) {
  useEffect(() => {
    const scriptId = `structured-data-${type.toLowerCase()}`;
    
    // Remove existing script if present
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    // Determine which data to use
    let structuredData: Record<string, unknown>;
    if (data) {
      structuredData = data;
    } else if (type === "Person") {
      structuredData = personData;
    } else if (type === "WebSite") {
      structuredData = websiteData;
    } else {
      return;
    }

    // Create and inject script
    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, data]);

  return null;
}

// Helper for blog post articles
export function ArticleStructuredData({
  title,
  description,
  image,
  publishedTime,
  author = "Brian J Kopp",
  url,
}: {
  title: string;
  description: string;
  image?: string;
  publishedTime: string;
  author?: string;
  url: string;
}) {
  const articleData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: description,
    image: image || "https://brianjkopp.co/og-image.png",
    datePublished: publishedTime,
    author: {
      "@type": "Person",
      name: author,
      url: "https://brianjkopp.co",
    },
    publisher: {
      "@type": "Person",
      name: "Brian J Kopp",
      url: "https://brianjkopp.co",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": url,
    },
  };

  return <StructuredData type="Article" data={articleData} />;
}

// Breadcrumb structured data for navigation
export function BreadcrumbStructuredData({
  items,
}: {
  items: { name: string; url: string }[];
}) {
  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  useEffect(() => {
    const scriptId = "structured-data-breadcrumb";
    
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(breadcrumbData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(scriptId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [items]);

  return null;
}
